import captureSentryException from '@/features/app/functions/captureSentryException';
import useFeatureFlags from '@/features/feature-flags/hooks/useFeatureFlags';
import getPushTokenProvider from '@/features/push/providers/get-push-token-provider';
import usePushDeviceRegistrationStore from '@/features/push/stores/push-device-registration';
import useNotificationSettingsQuery from '@/features/user/queries/useNotificationSettingsQuery';
import useAxios from '@/shared/hooks/useAxios';
import { useMutation } from '@tanstack/react-query';
import useUnregisterPushDeviceMutation from './useUnregisterPushDeviceMutation';

export default function useSyncPushDeviceMutation() {
  const axios = useAxios();
  const featureFlags = useFeatureFlags();
  const { data: notificationSettings } = useNotificationSettingsQuery();
  const { mutate: unregisterPushDevice } = useUnregisterPushDeviceMutation();
  const { setRegistration, registration } = usePushDeviceRegistrationStore();

  return useMutation({
    async mutationFn() {
      try {
        if (!featureFlags.push.enabled || !notificationSettings) return;
        if (!notificationSettings.some((setting) => setting.viaPush === true)) {
          unregisterPushDevice();
          return;
        }

        const provider = getPushTokenProvider();
        const { fcmToken, deviceType } = await provider.getToken();
        if (!fcmToken) return;

        if (
          registration?.id &&
          registration.fcmToken === fcmToken &&
          registration.deviceType === deviceType
        )
          return;

        const response = await axios.post('/api/v1/user-device', {
          fcmToken,
          deviceType,
        });

        setRegistration({
          id: response.data.id,
          fcmToken,
          deviceType,
        });
      } catch (error) {
        captureSentryException(error, {
          tags: { feature: 'push.sync' },
        });
      }
    },
  });
}
